<template>
  <div class="nota">
    <div v-if="loading" class="nota-state">Menyiapkan nota...</div>
    <div v-else-if="error" class="nota-state">{{ error }}</div>

    <div v-else align="center" class="nota-body">
      <b style="font-size: 1.4em">{{ storeName }}</b>
      <br />
      {{ storeAddress }}
      <div>
        <table width="100%">
          <tr>
            <td colspan="3"><hr /></td>
          </tr>
          <tr>
            <td colspan="2" valign="top">
              <b>{{ invoice.transactionNumber }}</b>
            </td>
          </tr>
          <tr>
            <td valign="top">
              <b>{{
                dateTimeFormat(invoice.transactionDate, "DD MMM yyyy")
              }}</b>
            </td>
            <td align="right" valign="top">
              <b>{{ dateTimeFormat(invoice.transactionDate, "HH:mm") }}</b>
            </td>
          </tr>
          <tr>
            <td valign="top">Pelanggan</td>
            <td align="right" valign="top">
              {{ invoice.customer?.label || "-" }}
            </td>
          </tr>
          <tr>
            <td valign="top">Kasir</td>
            <td align="right" valign="top">
              {{ invoice.created?.createdBy || "-" }}
            </td>
          </tr>
          <tr>
            <td colspan="3"><hr /></td>
          </tr>
        </table>
      </div>
      <table width="100%">
        <tr
          v-for="(cart, i) in invoice.carts"
          v-bind:key="i"
          style="text-align: left"
        >
          <td align="left" colspan="2">
            <b>{{ cart.productName }}</b
            ><br />
            <fragment v-if="!isNullOrEmpty(cart.productVariantName)">
              <small>{{ cart.productVariantName }}</small
              ><br />
            </fragment>
            {{ cart.quantity }}x{{ numberFormat(cart.price) }}
            <!-- Coret hanya untuk diskon per item. Diskon se-transaksi
                 (invoice.discountId terisi) disebar ke tiap cart.discount, jadi
                 tampilkan total kotor; diskon muncul di baris "Diskon" ringkasan. -->
            <div
              v-if="cart.discount > 0 && !invoice.discountId"
              style="float: right; margin-bottom: 4px"
            >
              <small style="text-decoration: line-through">{{
                numberFormat(cart.quantity * cart.price)
              }}</small>
              {{ numberFormat(cart.fixedPrice) }}
            </div>
            <div v-else style="float: right; margin-bottom: 4px">
              {{ numberFormat(cart.quantity * cart.price) }}
            </div>
          </td>
        </tr>

        <tr>
          <td colspan="2"><hr /></td>
        </tr>
        <tr style="text-align: left">
          <td>Sub Total</td>
          <td align="right">{{ numberFormat(invoice.subTotal) }}</td>
        </tr>
        <tr v-if="invoice.taxPercentage > 0" style="text-align: left">
          <td>Pajak ({{ invoice.taxPercentage }}%)</td>
          <td align="right">{{ numberFormat(invoice.tax) }}</td>
        </tr>
        <tr style="text-align: left">
          <td>Diskon</td>
          <td align="right">{{ numberFormat(invoice.discount) }}</td>
        </tr>

        <tr>
          <td colspan="2"><hr /></td>
        </tr>
        <tr style="text-align: left">
          <td><b>Total Transaksi</b></td>
          <td align="right">
            <b>{{ numberFormat(invoice.grandTotal) }}</b>
          </td>
        </tr>
        <tr style="text-align: left">
          <td>
            <b>{{ invoice.paymentMethod?.label }}</b>
          </td>
          <td align="right">
            <b>{{ numberFormat(invoice.paymentAmount) }}</b>
          </td>
        </tr>
        <tr v-if="invoice.paymentChange > 0" style="text-align: left">
          <td><b>Kembalian</b></td>
          <td align="right">
            <b>{{ numberFormat(invoice.paymentChange) }}</b>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import { dateTimeFormat, numberFormat, isNullOrEmpty } from "@/core/utils";

export default {
  data: () => ({
    loading: true,
    error: null,
    invoice: {
      carts: [],
    },
  }),
  computed: {
    ...mapGetters(["store"]),
    // Nota dicetak untuk store yang sedang aktif, tapi nama store diambil dari
    // transaksinya supaya tetap benar walau store aktif sudah berpindah.
    storeName() {
      const self = this;
      return self.invoice.store?.label || self.store?.storeName;
    },
    storeAddress() {
      const self = this;
      return self.store?.address;
    },
  },
  created() {
    const self = this;
    self.getTransactionById();
  },
  methods: {
    dateTimeFormat,
    numberFormat,
    isNullOrEmpty,
    getTransactionById() {
      const self = this;
      const { storeId, transactionId } = self.$route.params;

      if (isNullOrEmpty(storeId) || isNullOrEmpty(transactionId)) {
        self.loading = false;
        self.error = "Nota tidak ditemukan";

        return;
      }

      self.$store
        .dispatch("apis/get", {
          url: `/transaction/${storeId}/${transactionId}`,
        })
        .then((response) => {
          if (response.error) {
            self.error = response.message;

            return;
          }

          self.invoice = {
            transactionNumber: response.data.transactionNumber,
            transactionDate: response.data.created?.createdAt,
            store: response.data.store,
            customer: response.data.customer,
            subTotal: response.data.subTotal,
            taxPercentage: response.data.taxPercentage,
            tax: response.data.tax,
            discountId: response.data.discountId,
            discount: response.data.discount,
            grandTotal: response.data.grandTotal,
            paymentAmount: response.data.paymentAmount,
            paymentChange: response.data.paymentChange,
            paymentRemaining: response.data.paymentRemaining,
            paymentMethod: response.data.paymentMethod,
            carts: response.data.carts || [],
            created: response.data.created,
          };

          return self.setPrintBill(storeId, transactionId);
        })
        .finally(() => {
          self.loading = false;

          if (self.error) return;

          // Nota harus selesai ter-render sebelum dialog print browser dibuka.
          self.$nextTick(() => {
            window.print();
            setTimeout(() => window.close(), 500);
          });
        });
    },
    // Menandai nota sudah dicetak. Kegagalan penandaan tidak boleh membatalkan cetak.
    setPrintBill(storeId, transactionId) {
      const self = this;

      return self.$store.dispatch("apis/put", {
        url: `/transaction/${storeId}/print/${transactionId}`,
      });
    },
  },
};
</script>

<style scoped>
.nota {
  padding: 5px;
  margin-left: auto;
  margin-right: auto;
  max-width: 300px;
  font-family: Poppins, Helvetica, sans-serif;
  font-size: 12px;
  color: #000;
  background: #fff;
}

.nota table {
  font-size: 12px;
}

.nota-body {
  padding: 5px;
  max-width: 300px;
}

.nota-state {
  padding: 24px 0;
  text-align: center;
}
</style>

<!-- Override print global Metronic (@page a3 + body/container min-width 992px)
     yang membuat nota di-scale mengecil pada printer thermal 80mm. Blok ini
     sengaja TIDAK scoped supaya bisa menimpa @page, html, dan body. -->
<style media="print">
@page {
  size: 80mm auto;
  margin: 0;
}

html,
body {
  width: 80mm !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  background: #fff !important;
}

body .nota,
body .nota-body {
  width: 80mm !important;
  max-width: 80mm !important;
  min-width: 0 !important;
  margin: 0 !important;
}
</style>
